import { ModuleShell } from '../components/ModuleShell';
import { DataTable, type Column } from '../components/DataTable';
import { Mono, StatusChip } from '../components/StatusChip';
import { getCrashHistory, clearCrashHistory } from '../components/ErrorBoundary';
import { timeAgo } from '../api/format';
import type { DiagnosticsResponse, ClientStatus, CollectorHealth, ModuleState } from '../api/types';

interface Props {
  moduleState: ModuleState<DiagnosticsResponse> & { refetch: () => void };
  crashCount?: number;
  version?: string;
  collectors?: CollectorHealth[];
}

export function DiagnosticsModule({ moduleState, crashCount = 0, version, collectors = [] }: Props) {
  const data = moduleState.data;
  const shadow = data?.missive_shadow;
  const clientStatus = data?.client_status || [];
  const crashes = getCrashHistory();

  const statusCols: Column<ClientStatus>[] = [
    { key: 'name', label: 'Client', width: '150px', sortValue: r => r.name, render: r => <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{r.name}</span> },
    { key: 'active', label: 'Active', align: 'right', width: '80px', render: r => <StatusChip label={r.is_active ? 'Yes' : 'No'} variant={r.is_active ? 'success' : 'error'} showIcon /> },
    { key: 'reason', label: 'Reason', render: r => <span style={{ fontSize: 11, color: 'var(--text-ghost)' }}>{r.active_reason}</span> },
    { key: 'evaluated', label: 'Evaluated', align: 'right', sortValue: r => r.last_evaluated_at, render: r => <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{timeAgo(r.last_evaluated_at)}</span> },
  ];

  return (
    <ModuleShell name="diagnostics" title="Diagnostics" status={moduleState.status} error={moduleState.error} dataFreshness={moduleState.dataFreshness} fetchedAt={moduleState.fetchedAt} isStale={moduleState.isStale} truth={moduleState.truth} staleReason={moduleState.staleReason} onRetry={moduleState.refetch}
      headerRight={<StatusChip label="Admin" variant="muted" showIcon />}>

      {/* Build + System */}
      <Section title="System Info">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, padding: 16, background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
          {[
            { l: 'VERSION', v: version || '?', c: 'var(--text-secondary)' },
            { l: 'FETCH ERRORS', v: String(crashCount), c: crashCount > 0 ? 'var(--orange)' : 'var(--green)' },
            { l: 'UI CRASHES', v: String(crashes.length), c: crashes.length > 0 ? 'var(--red)' : 'var(--green)' },
            { l: 'DATA AS OF', v: moduleState.dataFreshness?.last_success ? timeAgo(moduleState.dataFreshness.last_success) : 'never' },
            { l: 'LAST RUN', v: moduleState.dataFreshness?.last_run_status?.split(' at ')[0] || '?', c: moduleState.dataFreshness?.last_run_status?.startsWith('completed') ? 'var(--green)' : 'var(--red)' },
          ].map(x => (
            <div key={x.l} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 9, color: 'var(--text-ghost)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{x.l}</div>
              <Mono color={x.c || 'var(--text-secondary)'} style={{ fontSize: 14, fontWeight: 700 }}>{x.v}</Mono>
            </div>
          ))}
        </div>
      </Section>

      {/* Collector Detail */}
      {collectors.length > 0 && (
        <Section title="Collector Detail">
          <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-raised)' }}>
                  {['Collector', 'Last Completed', 'Freshness', 'Runs 24h', 'Errors 24h', 'Error Rate', 'Status'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', fontSize: 9, fontWeight: 700, color: 'var(--text-ghost)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'left', borderBottom: '1px solid var(--border-subtle)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {collectors.map(c => (
                  <tr key={c.collector} style={{ borderBottom: '1px solid var(--border-faint)' }}>
                    <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{c.collector.replace('sync_', '').replace(/_/g, ' ')}</td>
                    <td style={{ padding: '8px 12px' }}><Mono color="var(--text-muted)" style={{ fontSize: 11 }}>{c.last_completed || 'never'}</Mono></td>
                    <td style={{ padding: '8px 12px' }}><StatusChip label={c.freshness} variant={c.freshness === 'ok' ? 'success' : 'stale'} showIcon /></td>
                    <td style={{ padding: '8px 12px' }}><Mono color="var(--text-muted)" style={{ fontSize: 11 }}>{c.runs_24h}</Mono></td>
                    <td style={{ padding: '8px 12px' }}><Mono color={c.errors_24h > 0 ? 'var(--red)' : 'var(--text-muted)'} style={{ fontSize: 11 }}>{c.errors_24h}</Mono></td>
                    <td style={{ padding: '8px 12px' }}><Mono color={c.error_rate_24h > 0.5 ? 'var(--red)' : c.error_rate_24h > 0 ? 'var(--yellow)' : 'var(--green)'} style={{ fontSize: 11 }}>{(c.error_rate_24h * 100).toFixed(0)}%</Mono></td>
                    <td style={{ padding: '8px 12px' }}>
                      {c.consecutive_failures ? <StatusChip label="failing" variant="error" showIcon /> : c.health_warning ? <StatusChip label="warning" variant="warning" showIcon /> : <StatusChip label="ok" variant="success" showIcon />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* UI Crash Log */}
      {crashes.length > 0 && (
        <Section title="UI Crash Log">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button onClick={() => { clearCrashHistory(); moduleState.refetch(); }} style={{ padding: '3px 10px', fontSize: 10, background: 'var(--bg-active)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', color: 'var(--text-ghost)', cursor: 'pointer' }}>Clear log</button>
          </div>
          <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
            {crashes.slice(-20).reverse().map((c, i) => (
              <div key={i} style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-faint)', fontSize: 11 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600, color: 'var(--red)' }}>{c.module}</span>
                  <span style={{ color: 'var(--text-dim)' }}>{new Date(c.timestamp).toLocaleTimeString()}</span>
                </div>
                <div style={{ color: 'var(--text-ghost)', marginTop: 2, fontFamily: 'var(--font-mono)', fontSize: 10 }}>{c.error}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Shadow Truth */}
      {shadow && (
        <Section title="Missive Shadow Truth">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, padding: 16, background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)', marginBottom: 12 }}>
            {[
              { l: 'CANONICAL', v: shadow.canonical_total },
              { l: 'UNRESOLVED', v: shadow.unresolved_total, c: shadow.unresolved_total > 0 ? 'var(--yellow)' : 'var(--green)' },
              { l: 'TOTAL', v: shadow.total_interested_conversations, c: 'var(--blue-bright)' },
              { l: 'TRUNCATED', v: shadow.conversations_truncated ? 'Yes' : 'No', c: shadow.conversations_truncated ? 'var(--red)' : 'var(--green)' },
            ].map(x => (
              <div key={x.l} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 9, color: 'var(--text-ghost)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 2 }}>{x.l}</div>
                <Mono color={x.c || 'var(--text-primary)'} style={{ fontSize: 18, fontWeight: 700 }}>{typeof x.v === 'number' ? x.v : x.v}</Mono>
              </div>
            ))}
          </div>
          {shadow.unresolved_total > 0 && (
            <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', overflow: 'hidden', marginBottom: 12 }}>
              <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-raised)', fontSize: 10, fontWeight: 700, color: 'var(--text-ghost)', textTransform: 'uppercase' }}>Unresolved by Guess</div>
              {Object.entries(shadow.unresolved_by_guess).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px', borderBottom: '1px solid var(--border-faint)' }}>
                  <span style={{ fontSize: 12, color: k === 'unknown' ? 'var(--text-ghost)' : 'var(--text-secondary)', fontWeight: 600 }}>{k === 'unknown' ? '(unattributed)' : k}</span>
                  <Mono color="var(--yellow)">{v}</Mono>
                </div>
              ))}
            </div>
          )}
          <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
            <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-raised)', fontSize: 10, fontWeight: 700, color: 'var(--text-ghost)', textTransform: 'uppercase' }}>Canonical by Client</div>
            {Object.entries(shadow.by_client).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px', borderBottom: '1px solid var(--border-faint)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>{k}</span>
                <Mono color="var(--blue-bright)">{v}</Mono>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Client Status */}
      <Section title="Client Activity Status">
        <DataTable columns={statusCols} rows={clientStatus} rowKey={r => r.id} emptyMessage="No client status data" />
      </Section>
    </ModuleShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>{title}</h3>
      {children}
    </div>
  );
}
